<template>
	<div
		class="provider-card"
		v-if="data"
		@click="onProviderCardClick"
		:class="data.profiletype_s"
	>
		<div class="provider-img-wrapper">
			<avatar
				class="provider-img"
				:name="avatarName"
				:src="data.photourl || ''"
				align-center
				background="rgb(223, 242, 243)"
				color="rgba(51, 49, 50, 0.32)"
				:size="60"
			/>
		</div>
		<div class="info">
			<h5 class="provider-name">
				<a
					:href="data.provider_url_s"
					rel="nofollow"
					@click.stop="onProviderCardClick($event, 'nm')"
					ref="providerUrl"
				>
					{{ fullnameWithPrefix }}
				</a>
			</h5>
			<p class="specialties">
				<a
					:href="data.provider_url_s"
					rel="nofollow"
				>
					{{ getSpecialties }}
				</a>
			</p>
			<div class="ratings-container">
				<a
					:href="`${data.provider_url_s}#ratings`"
					rel="nofollow"
					class="ratings"
				>
					<div>
						<span style="margin-right: 8px;">
							{{ data.review_score.toFixed(1) }}
						</span>
						<Rate
							:aria-label="data.review_score"
							:model-value="data.review_score"
							size="medium"
							disabled
							show-number
						/>
					</div>
					<span
						v-if="getRatings"
						class="rating-score"
					>
						{{ getRatings }}
					</span>
				</a>
				<p
					class="experience"
					v-if="getExperience"
				>
					<icons name="experience" />
					<span>{{ getExperience }}</span>
				</p>
			</div>
		</div>
		<div class="spacer"></div>
		<div class="provider-ctas">
			<a
				class="webmd-button webmd-button--primary webmd-button--medium is-round"
				native-type="link"
				rel="nofollow"
				:href="`tel:${formattedPhone}`"
				type="primary"
				@click.stop="triggerOmniture('ph')"
				round
				v-if="formattedPhone"
			>
				<icons name="phone" />
				<span>{{ formattedPhone }}</span>
			</a>
			<a
				class="webmd-button webmd-button--medium"
				:class="formattedPhone ? 'webmd-button--text' : 'webmd-button--primary is-round'"
				native-type="link"
				rel="nofollow"
				:href="data.provider_url_s"
				type="text"
			>
				<span>View Full Profile</span>
			</a>
		</div>
	</div>
	<div
		class="provider-card skeleton"
		v-else
	>
		<div class="photo"></div>
		<div class="name"></div>
		<div class="specialty"></div>
		<div class="specialty"></div>
		<div class="specialty"></div>
		<div class="ctas"></div>
		<div class="ctas"></div>
	</div>
</template>
<script>
import { Rate } from 'webmd-elements';
import Avatar from './Avatar.vue';
import Icons from './Icons.vue';

export default {
	name: 'ProviderCard',
	components: {
		Avatar,
		Icons,
		Rate,
	},
	props: {
		data: {
			type: Object,
			default: null,
		},
		campaignData: {
			type: Object,
			default: null,
		},
	},
	data: () => ({
		MODULE_TYPE: {
			NO_CAMPAIGN: 'no_campaign',
			NO_CAMPAIGN_ENHANCED: 'no_campaign_enhanced',
			CAMPAIGN: 'campaign',
		},
	}),
	computed: {
		formattedPhone() {
			if (this.data.phonenumber) {
				let phone = this.data.phonenumber;
				phone = phone.replace(/[^\d]/g, '');
				return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
			}
			return '';
		},
		fullnameWithPrefix() {
			let degrees = this.data.degreeabbr
				? this.data.degreeabbr.filter((item) => item && item?.trim().length)
				: '';
			degrees = degrees ? `, ${degrees.join(', ')}` : '';
			let name = [this.data.suffix, this.data.fullname].filter(Boolean).join(' ');
			name = name.trim();
			name += degrees;
			return name;
		},
		avatarName() {
			return [this.data.firstname || '', this.data.lastname || '']
				.map((n) => n.replace(/\W/g, ''))
				.join(' ');
		},
		getSpecialties() {
			return this.data.specialties?.filter(Boolean).join(', ');
		},
		getRatings() {
			const ratings = this.data.review_count_d;
			if (ratings) {
				return `${ratings} Rating${ratings > 1 ? 's' : ''}`;
			}
			return '';
		},
		getExperience() {
			const exp = this.data.experience_years ?? 0;
			if (exp) {
				return `${exp} Year${exp > 1 ? 's' : ''} Exp`;
			}
			return '';
		},
	},
	methods: {
		onProviderCardClick(_, mlink) {
			if (!mlink && this.$refs.providerUrl?.click) {
				this.$refs.providerUrl.click();
			} else {
				this.triggerOmniture(mlink);
			}
		},
		triggerOmniture(mlink) {
			const profileType = this.data.profiletype_s;
			const providerId = this.data.providerid;
			const { campaignId } = this.campaignData;
			let mmodule = 'pdtrav1-';

			switch (profileType) {
				case 'Basic':
					mmodule = `pd-trav1-b-${providerId}`;
					break;
				case 'Enhanced':
					mmodule += `e-${providerId}`;
					break;
				case 'Sponsored':
					mmodule += campaignId;
					break;
				default:
					break;
			}
			if (window.globalMetrics) {
				window.globalMetrics.cookie?.set('wb.pdicd', mmodule);
			} else {
				this.$vsetCookie('wb.pdicd', mmodule);
			}
			
			this.$vwmdTrack(
				{ moduleName: `${mmodule}_${mlink}` },
				{
					metrics: { 'wb.pdicd': mmodule },
					resetMetrics: true,
				},
			);
			if (mlink === 'ph') {
				setTimeout(() => {
					window.location.href = this.data.provider_url_s;
				}, 0);
			}
		},
	},
};
</script>
<style lang="scss" scoped>
@import url('~webmd-elements/lib/theme-chalk/button.css');

.provider-card {
	background-color: #fff;
	border: 1px solid #CCCCE7;
	border-radius: 8px;
	padding: 1rem;
	text-align: center;
	cursor: pointer;
	height: 100%;
	display: flex;
	flex-direction: column;
	transition: transform 0.15s ease-in;

	@media screen and (max-width: 767px) {
		position: relative;

		.info {
			padding-left: 72px;
			text-align: left;
			display: flex;
			flex-direction: column;
			align-items: flex-start;
		}
	}

	&:hover {
		transform: translateY(-5px);
		box-shadow: 0 4px 8px rgba($color: #002168, $alpha: 0.2);
		transition: transform 0.25s ease-in;
	}

	.spacer {
		height: 100%;
		display: flex;
		flex: 1;
	}

	.provider-img-wrapper {
		@media screen and (max-width: 767px) {
			position: absolute;
		}

		.vue-avatar--wrapper {
			margin-bottom: 8px !important;
		}
	}

	.provider-name {
		font-weight: 700;
		font-size: 18px;
		line-height: 23px;
		color: var(--care-text-black);
		margin: 0;
		margin-bottom: 4px;

		a {
			color: inherit !important;
			text-decoration: underline !important;

			&:not(:hover) {
				text-decoration: none !important;
			}
		}
	}

	.specialties,
	.ratings-container {
		font-weight: 400;
		font-size: 13px;
		line-height: 15px;
		text-align: center;
		color: var(--care-text-black);

		a {
			color: inherit !important;
			text-decoration: underline !important;

			&:not(:hover) {
				text-decoration: none !important;
			}
		}
	}

	.ratings-container {
		display: flex;
		flex-flow: row wrap;
		column-gap: 6px;
		row-gap: 6px;
		justify-content: center;
		align-items: center;
		margin-bottom: 12px;

		.rating-score {
			color: var(--care-text-lightblue);
		}

		& > * {
			flex-shrink: 1;

			&:first-child {
				flex-grow: 0;
			}
		}

		& > * {
			display: flex;
			align-items: center;
			justify-content: center;

			svg {
				margin-right: 5px;
			}

			span {
				line-height: 15px;
			}
		}

		@media screen and (max-width: 1024px) {
			flex-direction: column;
			align-items: center;
		}

		@media screen and (max-width: 767px) {
			align-items: flex-start;
		}
	}

	.provider-ctas {
		margin-top: 1.25rem;

		.webmd-button {
			width: 100%;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			text-decoration: underline !important;

			&:not(:hover) {
				text-decoration: none !important;
			}

			&.webmd-button--primary {
				background-color: var(--care-text-lightblue);
			}

			&.webmd-button--text {
				padding: 0;
				margin-top: 12px;
				width: auto;
				font-weight: 600;
				color: var(--care-text-lightblue);
			}

			&>span {
				display: inline-flex;
				align-items: center;
				justify-content: center;
			}

			& svg+span {
				margin-left: 8px;
			}
		}

		.webmd-button+.webmd-button {
			margin-left: 0;
		}
	}

	.ratings {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-flow: row wrap;
		row-gap: 6px;

		@media screen and (max-width: 767px) {
			justify-content: flex-start;
		}

		& > * {
			flex-shrink: 1;

			&:first-child {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-grow: 0;
			}
		}
	}

	.specialties {
		margin: 0;
		margin-bottom: 12px;

		@media screen and (min-width: 768px) and (max-width: 1023px) {
			margin-bottom: 4px;
		}

		@media screen and (max-width: 767px) {
			text-align: left;
		}
	}

	&.skeleton {
		align-items: center;

		@media screen and (max-width: 767px) {
			align-items: flex-start;
			padding-left: 90px;
		}

		>* {
			margin-bottom: 0.25rem;
			background-color: #eee;
			border-radius: 4px;
			overflow: hidden;
			position: relative;

			@keyframes slide {
				from {
					transform: translate(-200px, -2rem) rotate(45deg);
				}

				to {
					transform: translate(300px, -2rem) rotate(45deg);
				}
			}

			&::after {
				content: '';
				position: absolute;
				width: 2rem;
				height: 10rem;
				background-color: #e6e6e6;
				transform: rotate(45deg);
				animation: slide 2s ease infinite forwards;
			}
		}

		.photo {
			width: 60px;
			height: 60px;
			border-radius: 30px;
			margin-bottom: 0.75rem;

			&::after {
				animation: slide 4s ease infinite forwards;
			}

			@media screen and (max-width: 767px) {
				position: absolute;
				left: 15px;
			}
		}

		.name {
			height: 22px;
			width: 70%;
		}

		.specialty {
			height: 15px;
			width: 90%;

			@media screen and (max-width: 1023px) {
				height: 20px;
			}
		}

		.specialty+.specialty {
			width: 80%;

			&::after {
				animation: slide 4s ease infinite forwards;
			}
		}

		.ctas {
			height: 42px;
			width: 100%;
			margin-top: 1rem;
		}

		.ctas+.ctas {
			height: 16px;
			margin-top: 0;

			&::after {
				animation: slide 4s ease infinite forwards;
			}
		}
	}
}
</style>
